import React from "react";
import { useFormContext } from "react-hook-form";

/**
 * Reusable contact fields (First Name, Last Name, Job Title, Email).
 * Follows the same patterns as LocationFields: fieldPrefix, control, errors, setValue,
 * and value/setter props for each field.
 *
 * @param {Object} props
 * @param {import("react-hook-form").Control} [props.control] - Form control (optional when used outside FormProvider)
 * @param {Object} [props.errors] - Form errors object keyed by field name (e.g. contactFirstName)
 * @param {Function} [props.setValue] - Form setValue (optional when used outside FormProvider)
 * @param {string} [props.fieldPrefix="contact"] - Prefix for form field names and error keys (e.g. "contact" -> contactFirstName, "additional_0_" -> additional_0_FirstName)
 * @param {string} [props.firstName] - First name value
 * @param {Function} [props.setFirstName] - First name setter
 * @param {string} [props.lastName] - Last name value
 * @param {Function} [props.setLastName] - Last name setter
 * @param {string} [props.title] - Job title value
 * @param {Function} [props.setTitle] - Job title setter
 * @param {string} [props.email] - Email value
 * @param {Function} [props.setEmail] - Email setter
 * @param {string} [props.classes=""] - Extra CSS classes for the wrapper
 * @param {boolean} [props.fullWidth=false] - If true, do not constrain max width (sm:max-w-xl is omitted)
 * @param {boolean} [props.errorHighlight=true] - If false, do not apply red ring to fields on error (inline error message still shown)
 */
const ContactFields = ({
  control: controlProp,
  errors = {},
  setValue: setValueProp,
  fieldPrefix = "contact",
  firstName,
  setFirstName,
  lastName,
  setLastName,
  title,
  setTitle,
  email,
  setEmail,
  classes = "",
  fullWidth = false,
  errorHighlight = true,
}) => {
  const formContext = useFormContext?.();
  const control = controlProp ?? formContext?.control;
  const setValue = setValueProp ?? formContext?.setValue;
  const formErrors =
    errors && Object.keys(errors).length ? errors : formContext?.formState?.errors ?? {};

  const fieldName = (suffix) => `${fieldPrefix}${suffix}`;
  const err = (key) => formErrors[key];
  const hasErr = (key) => !!err(key);
  const errMsg = (key) => {
    const e = err(key);
    return e?.message ?? e;
  };

  const inputBaseClass =
    "block flex-1 border-0 bg-transparent py-1.5 pl-3 text-gray-900 placeholder:text-gray-500 focus:ring-0 focus:outline-none sm:text-sm sm:leading-6";
  const wrapperClass = (key) =>
    `flex rounded-md shadow-sm ring-1 ring-inset ${errorHighlight && hasErr(key) ? "ring-red-300 ring-2" : "ring-gray-200"} focus-within:ring-2 focus-within:ring-inset ${errorHighlight && hasErr(key) ? "focus-within:ring-red-500" : "focus-within:ring-indigo-600"}`;

  const handleChange = (field, value, setter, formKey) => {
    setter?.(value);
    setValue?.(formKey, value);
  };

  return (
    <div className={`${fullWidth ? "" : "sm:max-w-xl"} ${classes}`}>
      <div className="grid grid-cols-1 gap-y-4">
        {/* First Name and Last Name */}
        <div className="flex gap-4">
          <div className="flex-1">
            <div className={wrapperClass(fieldName("FirstName"))}>
              <input
                type="text"
                id={`${fieldPrefix}-first-name`}
                name={fieldName("FirstName")}
                autoComplete="given-name"
                value={firstName ?? ""}
                onChange={(e) =>
                  handleChange(
                    "firstName",
                    e.target.value,
                    setFirstName,
                    fieldName("FirstName"),
                  )
                }
                className={inputBaseClass}
                placeholder="First Name"
              />
            </div>
            {err(fieldName("FirstName")) && (
              <p className="mt-2 text-sm text-red-600">
                {errMsg(fieldName("FirstName"))}
              </p>
            )}
          </div>
          <div className="flex-1">
            <div className={wrapperClass(fieldName("LastName"))}>
              <input
                type="text"
                id={`${fieldPrefix}-last-name`}
                name={fieldName("LastName")}
                autoComplete="family-name"
                value={lastName ?? ""}
                onChange={(e) =>
                  handleChange(
                    "lastName",
                    e.target.value,
                    setLastName,
                    fieldName("LastName"),
                  )
                }
                className={inputBaseClass}
                placeholder="Last Name"
              />
            </div>
            {err(fieldName("LastName")) && (
              <p className="mt-2 text-sm text-red-600">
                {errMsg(fieldName("LastName"))}
              </p>
            )}
          </div>
        </div>

        {/* Job Title */}
        <div>
          <div className={wrapperClass(fieldName("Title"))}>
            <input
              type="text"
              id={`${fieldPrefix}-title`}
              name={fieldName("Title")}
              autoComplete="organization-title"
              value={title ?? ""}
              onChange={(e) =>
                handleChange(
                  "title",
                  e.target.value,
                  setTitle,
                  fieldName("Title"),
                )
              }
              className={inputBaseClass}
              placeholder="Job Title"
            />
          </div>
          {err(fieldName("Title")) && (
            <p className="mt-2 text-sm text-red-600">
              {errMsg(fieldName("Title"))}
            </p>
          )}
        </div>

        {/* Email */}
        <div>
          <div className={wrapperClass(fieldName("Email"))}>
            <input
              type="email"
              id={`${fieldPrefix}-email`}
              name={fieldName("Email")}
              autoComplete="email"
              value={email ?? ""}
              onChange={(e) =>
                handleChange(
                  "email",
                  e.target.value,
                  setEmail,
                  fieldName("Email"),
                )
              }
              className={inputBaseClass}
              placeholder="Email"
            />
          </div>
          {err(fieldName("Email")) && (
            <p className="mt-2 text-sm text-red-600">
              {errMsg(fieldName("Email"))}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContactFields;
